// NOTE: Sync with apps/portal/src/router/schema/shipments-search.ts.
import { GetShipmentsFilterStatusesItem as ShipmentStatus } from '@uturn/api/v2';
import type { TFunction } from 'i18next';
import { TabLink } from '../components/page/tabs';
import { FileRoutesByTo } from '../routeTree.gen';

type TFunc = TFunction<'translation', undefined, 'translation'>;

/** see CancelShipmentsParams or DeleteGroupByIdParams */
export type DefaultFilters = {
	/**
	 * shipment status
	 */
	'filter.statuses'?: ShipmentStatus[];
	/**
	 * // FIXME: "Exclude all groups" filter is broken.
	 * The ids of which groups to include. Ungrouped shipments are always included.
	 * If empty only ungrouped shipments are returned.
	 * If null no filter is applied.
	 *
	 *
	 * @todo // TODO: Empty meaning [] and null meaning undefined in JS, right?
	 * Why does the GroupFilers component uses '' to exclude Groups from shipment's
	 * list then? and instead, would an undefined value achieve this the desired
	 * behaviour?
	 */
	'filter.groups'?: number[];
};

export type StatusTab =
	| 'published'
	| 'assigned'
	| 'drafted'
	| 'archived'
	| 'groups';

export type ShipmentsStatusTab = TabLink & {
	navTab: StatusTab;
	defaultFilters: DefaultFilters;
};

export type ShipmentsStatusTabRaw = ShipmentsStatusTab & { titleTKey: string };

export type StatusOption = {
	label: string;
	value: ShipmentStatus;
	navTab: StatusTab;
	count?: number;
};

export type StatusOptionRaw = StatusOption & {
	labelTKey: string;
};

export const STATUS_OPTIONS: StatusOptionRaw[] = [
	{
		labelTKey: 'pages.shipments.filters.status.published',
		label: 'Published',
		value: ShipmentStatus.PUBLISHED,
		navTab: 'published',
	},
	{
		labelTKey: 'pages.shipments.filters.status.quoted',
		label: 'Quoted',
		value: ShipmentStatus.QUOTED,
		navTab: 'published',
	},
	{
		labelTKey: 'pages.shipments.filters.status.carrierAssigned',
		label: 'Carrier assigned',
		value: ShipmentStatus.CARRIER_ASSIGNED,
		navTab: 'assigned',
	},
	{
		labelTKey: 'pages.shipments.filters.status.costDeclared',
		label: 'Cost declared',
		value: ShipmentStatus.COST_DECLARED,
		navTab: 'assigned',
	},
	{
		labelTKey: 'pages.shipments.filters.status.executed',
		label: 'Executed',
		value: ShipmentStatus.EXECUTED,
		navTab: 'assigned',
	},
	{
		labelTKey: 'pages.shipments.filters.status.concept',
		label: 'Concept',
		value: ShipmentStatus.CONCEPT,
		navTab: 'drafted',
	},
	{
		labelTKey: 'pages.shipments.filters.status.cancelled',
		label: 'Cancelled',
		value: ShipmentStatus.CANCELLED,
		navTab: 'archived',
	},
	{
		labelTKey: 'pages.shipments.filters.status.completed',
		label: 'Completed',
		value: ShipmentStatus.COMPLETED,
		navTab: 'archived',
	},
];

export const getRawDefaultStatusOptions = (
	navTab: StatusTab,
): StatusOption[] => {
	return STATUS_OPTIONS.filter((item) => item.navTab === navTab).map(
		(item) => ({
			label: item.label,
			value: item.value,
			navTab: item.navTab,
		}),
	);
};

export const getRawDefaultStatuses = (navTab: StatusTab): ShipmentStatus[] => {
	return STATUS_OPTIONS.filter((item) => item.navTab === navTab).map(
		(item) => item.value,
	);
};

export const getRawDefaultStatusFilters = (
	navTab: StatusTab,
): DefaultFilters => {
	if (navTab === 'groups') {
		return {
			'filter.groups': undefined,
		};
	}
	return {
		'filter.statuses': getRawDefaultStatuses(navTab),
	};
};

export const SHIPMENTS_STATUS_TABS: ShipmentsStatusTabRaw[] = [
	{
		titleTKey: 'pages.shipments.tabs.published',
		title: 'Published',
		to: '/shipments',
		navTab: 'published',
		defaultFilters: getRawDefaultStatusFilters('published'),
	},
	{
		titleTKey: 'pages.shipments.tabs.assigned',
		title: 'Assigned',
		to: '/shipments/assigned',
		navTab: 'assigned',
		defaultFilters: getRawDefaultStatusFilters('assigned'),
	},
	{
		titleTKey: 'pages.shipments.tabs.drafted',
		title: 'Drafted',
		to: '/shipments/drafted',
		navTab: 'drafted',
		defaultFilters: getRawDefaultStatusFilters('drafted'),
	},
	{
		titleTKey: 'pages.shipments.tabs.archived',
		title: 'Archived',
		to: '/shipments/archived',
		navTab: 'archived',
		defaultFilters: getRawDefaultStatusFilters('archived'),
	},
	{
		titleTKey: 'pages.shipments.tabs.groups',
		title: 'Groups',
		to: '/shipments/groups',
		navTab: 'groups',
		defaultFilters: getRawDefaultStatusFilters('groups'),
	},
];

export const GROUP_SHIPMENTS_STATUS_TABS: ShipmentsStatusTabRaw[] = [
	{
		titleTKey: 'general.shipment-status.published',
		title: 'Published',
		to: '/shipments/groups/$groupId',
		navTab: 'published',
		defaultFilters: getRawDefaultStatusFilters('published'),
	},
	{
		titleTKey: 'general.shipment-status.assigned',
		title: 'Assigned',
		to: '/shipments/groups/$groupId/assigned',
		navTab: 'assigned',
		defaultFilters: getRawDefaultStatusFilters('assigned'),
	},
	{
		titleTKey: 'general.shipment-status.drafted',
		title: 'Drafted',
		to: '/shipments/groups/$groupId/drafted',
		navTab: 'drafted',
		defaultFilters: getRawDefaultStatusFilters('drafted'),
	},
	{
		titleTKey: 'general.shipment-status.archived',
		title: 'Archived',
		to: '/shipments/groups/$groupId/archived',
		navTab: 'archived',
		defaultFilters: getRawDefaultStatusFilters('archived'),
	},
];

export const getShipmentsStatusTabs = (
	t: TFunc,
	pages: ShipmentsStatusTabRaw[],
): ShipmentsStatusTab[] => {
	return pages.map((page) => ({
		title: t(page.titleTKey, typeof page.title === 'string' ? page.title : ''),
		to: page.to,
		navTab: page.navTab,
		defaultFilters: getDefaultStatusFilters(t, page.navTab),
	}));
};

export const DEFAULT_SHIPMENT_TAB: ShipmentsStatusTab = {
	title: SHIPMENTS_STATUS_TABS[0].title,
	to: SHIPMENTS_STATUS_TABS[0].to,
	navTab: SHIPMENTS_STATUS_TABS[0].navTab,
	defaultFilters: SHIPMENTS_STATUS_TABS[0].defaultFilters,
	active: true,
};

export const DEFAULT_ACTIVE_STATUS_TAB = DEFAULT_SHIPMENT_TAB.navTab;

export const DEFAULT_STATUS_OPTIONS = getRawDefaultStatusOptions(
	DEFAULT_ACTIVE_STATUS_TAB,
);

export const DEFAULT_STATUS_FILTERS: DefaultFilters = {
	'filter.statuses': DEFAULT_STATUS_OPTIONS.map((item) => item.value),
};

export const getStatusFilters = (t: TFunc): StatusOption[] => {
	return STATUS_OPTIONS.map((item) => ({
		label: t(item.labelTKey, item.label),
		value: item.value,
		navTab: item.navTab,
	}));
};

export const getDefaultStatusOptions = (
	t: TFunc,
	navTab: StatusTab,
): StatusOption[] => {
	return getStatusFilters(t).filter((item) => item.navTab === navTab);
};

export const getDefaultStatuses = (
	t: TFunc,
	navTab: StatusTab,
): ShipmentStatus[] => {
	return getStatusFilters(t)
		.filter((item) => item.navTab === navTab)
		.map((item) => item.value);
};

export const getDefaultStatusFilters = (
	t: TFunc,
	navTab: StatusTab,
): DefaultFilters => {
	if (navTab === 'groups') {
		return {
			'filter.groups': undefined,
		};
	}
	return {
		'filter.statuses': getDefaultStatuses(t, navTab),
	};
};

export type ParentLocation = {
	href: keyof FileRoutesByTo | '.' | '..';
	params?: Record<string, unknown>;
	search?: Record<string, unknown>;
};
